import { FC, useState, useCallback, useEffect, useMemo } from 'react'
import { notification } from 'antd'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { SerializedError } from '@reduxjs/toolkit'
import useFormProcessor from '@hooks/useFormProcessor'
import { usePostScormMutation } from '@services/scorms'
import {
  usePostFinalTestCourseThemeMutation,
  usePutFinalTestCourseThemeMutation,
} from '@services/courseThemes'
import { usePatchCourseFinalTestMutation } from '@services/courses'
import { File } from '@models/file'
import AddNewItemModal from '@templates/AddNewItemModal'
import { fileField, numberField } from '@templates/AddNewItemModal/templates'
import {
  parseFormHoursToMinutes,
  themeMinutesToFormHours,
} from '@utils/themeHoursTime'
import { DownloadOutlined, CopyOutlined } from '@ant-design/icons'
import ButtonStyle from '@enums/ButtonStyle'
import {
  InstructionBlock,
  InstructionList,
  CodeSnippet,
  ScormFinalTestModalFrame,
  SnippetButtons,
  SnippetActionButton,
} from './styled'

const FINAL_TEST_TITLE = 'Итоговое тестирование'

const ISPRING_SCRIPT = 'parent.postMessage({type: \'ISPRING\',eventTime: new Date(),passingScore: \'%PASSING_SCORE%\',awardedScore: \'%AWARDED_SCORE%\',passingPercent: \'%PASSING_PERCENT%\',awardedPercent: \'%AWARDED_PERCENT%\',quizStatus: \'%QUIZ_STATUS%\',quizDuration: \'%QUIZ_DURATION%\',},\'*\',)'

const ISPRING_SCRIPT_DISPLAY = `parent.postMessage(
  {
    type: 'ISPRING',
    eventTime: new Date(),
    passingScore: '%PASSING_SCORE%',
    awardedScore: '%AWARDED_SCORE%',
    passingPercent: '%PASSING_PERCENT%',
    awardedPercent: '%AWARDED_PERCENT%',
    quizStatus: '%QUIZ_STATUS%',
    quizDuration: '%QUIZ_DURATION%',
  },
  '*',
)`
const INSTRUCTION_FILE_URL =
  process.env.REACT_APP_ISPRING_INSTRUCTION_URL || '/static/scorm/ispring-instruction.pdf'

type FormValues = {
  file?: File
  hoursTime?: string
}

type Props = {
  courseId: number
  courseIri: string
  handleCloseModal: () => void
  isEdit?: boolean
  initialHoursTime?: number
  initialFile?: File
  finalTestId?: number | string
}

type InstructionContentProps = {
  onCopyScript: () => void
  onDownloadInstruction: () => void
}

const instructionContent = ({
  onCopyScript,
  onDownloadInstruction,
}: InstructionContentProps) => (
  <InstructionBlock>
    <InstructionList>
      <li>Для корректного отображения собирайте SCORM с помощью инструмента iSpring.</li>
      <li>
        Чтобы прохождение корректно засчитывалось, обязательно в конце теста SCORM должен
        вызвать скрипт:
      </li>
    </InstructionList>
    <CodeSnippet>{ISPRING_SCRIPT_DISPLAY}</CodeSnippet>
    <SnippetButtons>
      <SnippetActionButton
        type="button"
        withoutUpperCase
        buttonStyle={ButtonStyle.WHITE}
        onClick={onDownloadInstruction}
        leftIcon={<DownloadOutlined />}
      >
        Скачать инструкцию
      </SnippetActionButton>
      <SnippetActionButton
        type="button"
        withoutUpperCase
        buttonStyle={ButtonStyle.WHITE}
        onClick={onCopyScript}
        leftIcon={<CopyOutlined />}
      >
        Скопировать скрипт
      </SnippetActionButton>
    </SnippetButtons>
  </InstructionBlock>
)

const ScormFinalTestModal: FC<Props> = ({
  courseId,
  courseIri,
  handleCloseModal,
  isEdit,
  finalTestId,
  initialHoursTime,
  initialFile,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<
    FetchBaseQueryError | SerializedError | undefined
  >(undefined)
  // Чем была отправка — созданием или редактированием. Экран успеха должен описывать
  // то, что уже произошло, а проп isEdit для этого не годится: он считается из курса,
  // и после успешного создания курс перезапрашивается, в нём появляется finalTest,
  // isEdit становится true и подменяет заголовок «добавлено» на «изменено» прямо под
  // открытым экраном успеха. null — отправки ещё не было, берём проп как есть.
  const [submittedAsEdit, setSubmittedAsEdit] = useState<boolean | null>(null)

  const [postScorm] = usePostScormMutation()
  const [postFinalTestCourseTheme] = usePostFinalTestCourseThemeMutation()
  const [putFinalTestCourseTheme] = usePutFinalTestCourseThemeMutation()
  const [patchCourseFinalTest, patchData] = usePatchCourseFinalTestMutation()

  const methods = useFormProcessor({
    apiErrors: submitError,
    defaultValues: {
      file: initialFile,
      hoursTime: themeMinutesToFormHours(initialHoursTime || 0),
    },
  })

  useEffect(() => {
    methods.reset({
      file: initialFile,
      hoursTime: themeMinutesToFormHours(initialHoursTime || 0),
    })
  }, [initialFile, initialHoursTime, methods])

  const fields = useMemo(
    () => [
      {
        fields: [
          fileField({
            name: 'file',
            label: 'SCORM',
            // На редактировании файл не обязателен: если новый zip не выбран,
            // сохраняем существующий SCORM и меняем только часы.
            isRequired: !isEdit,
            id: 'scorm-final-test-file',
            formats: ['zip'],
            maxSizeMegaBytes: 100,
            componentProps: {
              formatErrorMessage: 'Допустимый формат: zip',
              sizeErrorMessage: 'Файл больше 100Мб',
              uploadLabel: 'Загрузить файл',
            },
          }),
          numberField({
            name: 'hoursTime',
            label: 'Количество часов',
            isRequired: true,
            placeholder: 'Количество часов',
            componentProps: {
              maskOptions: {
                scale: 0,
              },
            },
          }),
        ],
      },
    ],
    [isEdit]
  )

  const handleCopyScript = useCallback(() => {
    navigator.clipboard.writeText(ISPRING_SCRIPT).then(() => {
      notification.success({
        message: 'Скопировано',
        duration: 2,
      })
    })
  }, [])

  const handleDownloadInstruction = useCallback(() => {
    fetch(INSTRUCTION_FILE_URL)
      .then((response) => response.blob())
      .then((blob) => {
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = 'ispring_scorm_instruction.pdf'
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()

        setTimeout(() => {
          link.parentNode?.removeChild(link)
          window.URL.revokeObjectURL(link.href)
        }, 100)
      })
  }, [])

  const handleSubmitForm = useCallback(
    async (form: FormValues) => {
      const fileIri = form.file?.['@id']

      setSubmittedAsEdit(Boolean(isEdit && finalTestId))

      // Редактирование без нового файла: меняем только часы, существующий
      // SCORM оставляем (courseTheme в PUT опционален — бэк его не трогает).
      if (!fileIri) {
        if (!(isEdit && finalTestId)) {
          return
        }

        setIsSubmitting(true)
        setSubmitError(undefined)

        try {
          await putFinalTestCourseTheme({
            id: finalTestId,
            title: FINAL_TEST_TITLE,
            hoursTime: parseFormHoursToMinutes(form.hoursTime),
            test: true,
            source: 'scorm',
          }).unwrap()
          handleCloseModal()
        } catch (error) {
          setSubmitError(error as FetchBaseQueryError | SerializedError)
        } finally {
          setIsSubmitting(false)
        }

        return
      }

      setIsSubmitting(true)
      setSubmitError(undefined)

      try {
        const scorm = await postScorm({
          title: FINAL_TEST_TITLE,
          file: fileIri,
          course: courseIri,
          type: 'scorm',
        }).unwrap()

        const basePayload = {
          title: FINAL_TEST_TITLE,
          courseTheme: scorm['@id'],
          hoursTime: parseFormHoursToMinutes(form.hoursTime),
          test: true,
        }

        if (isEdit && finalTestId) {
          await putFinalTestCourseTheme({
            ...basePayload,
            id: finalTestId,
            source: 'scorm',
          }).unwrap()
          handleCloseModal()
          return
        }

        const chapterTheme = await postFinalTestCourseTheme({
          ...basePayload,
          source: 'scorm',
        }).unwrap()

        await patchCourseFinalTest({
          id: courseId,
          finalTest: chapterTheme['@id'],
        }).unwrap()
      } catch (error) {
        setSubmitError(error as FetchBaseQueryError | SerializedError)
      } finally {
        setIsSubmitting(false)
      }
    },
    [courseId, courseIri, finalTestId, handleCloseModal, isEdit, patchCourseFinalTest, postFinalTestCourseTheme, postScorm, putFinalTestCourseTheme]
  )

  return (
    <ScormFinalTestModalFrame>
      <AddNewItemModal
        {...patchData}
        isLoading={isSubmitting || patchData.isLoading}
        handleCloseModal={handleCloseModal}
        onSubmitForm={handleSubmitForm}
        methods={methods}
        fields={fields}
        isEdit={submittedAsEdit ?? isEdit}
        className="scorm-final-test-modal"
        customFullTitle="Загрузить итоговое тестирование"
        successContentAddTitle="Итоговое тестирование успешно добавлено"
        successContentEditTitle="Итоговое тестирование успешно изменено"
        // Итоговый тест у курса один, поэтому «Добавить еще» на экране успеха не нужно:
        // кнопка вернула бы к форме, где файл уже необязателен, и сохранение молча
        // переписало бы только что созданный тест. Раньше её прятало перещёлкивание
        // isEdit после создания — то самое, которое здесь и чинится.
        withoutAddMore
        saveText={isEdit ? 'Сохранить' : 'Добавить'}
        contentAfterFields={instructionContent({
          onCopyScript: handleCopyScript,
          onDownloadInstruction: handleDownloadInstruction,
        })}
      />
    </ScormFinalTestModalFrame>
  )
}

export default ScormFinalTestModal
